// Conversores entre JSON estruturado e HTML para o editor de blog

export interface ContentBlock {
  type: 'paragraph' | 'heading' | 'list' | 'quote' | 'image' | 'video' | 'alert' | 'separator';
  text?: string;
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  ordered?: boolean;
  items?: string[];
  quote?: string;
  author?: string;
  url?: string;
  alt?: string;
  caption?: string;
  variant?: 'info' | 'warning' | 'success' | 'tip';
  align?: 'left' | 'center' | 'right' | 'justify';
}

/**
 * Converte o JSON estruturado para HTML editável no Tiptap
 */
export const jsonToHtml = (content: ContentBlock[]): string => {
  if (!content || !Array.isArray(content)) return '';

  return content.map(block => {
    switch (block.type) {
      case 'paragraph':
        const pAlign = block.align ? ` style="text-align: ${block.align};"` : '';
        return `<p${pAlign}>${block.text || '<br>'}</p>`;
      
      case 'heading':
        const level = block.level || 2;
        const hAlign = block.align ? ` style="text-align: ${block.align};"` : '';
        return `<h${level}${hAlign}>${block.text || ''}</h${level}>`;
      
      case 'list':
        if (!block.items || block.items.length === 0) return '';
        const listItems = block.items.map(item => `<li>${item}</li>`).join('');
        const tag = block.ordered ? 'ol' : 'ul';
        return `<${tag}>${listItems}</${tag}>`;
      
      case 'quote':
        const quoteText = block.quote || '';
        const author = block.author ? `<cite>${block.author}</cite>` : '';
        return `<blockquote><p>${quoteText}</p>${author}</blockquote>`;
      
      case 'image':
        return `<img src="${block.url || ''}" alt="${block.alt || ''}" />`;
      
      case 'alert':
        const variant = block.variant || 'info';
        return `<div class="alert-${variant}">${block.text || ''}</div>`;
      
      case 'separator':
        return '<hr />';
      
      default:
        return '';
    }
  }).join('');
};

/**
 * Converte o HTML do Tiptap de volta para JSON estruturado
 */
export const htmlToJson = (html: string): ContentBlock[] => {
  if (!html || html.trim() === '') return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const blocks: ContentBlock[] = [];

  const processNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const tagName = element.tagName.toLowerCase();

      switch (tagName) {
        case 'p': {
          const pContent = element.innerHTML.trim();
          // Extrair text-align do style
          const pStyle = element.getAttribute('style') || '';
          const pAlignMatch = pStyle.match(/text-align:\s*(left|center|right|justify)/);
          const pAlign = pAlignMatch ? pAlignMatch[1] as 'left' | 'center' | 'right' | 'justify' : undefined;
          
          // Preservar parágrafos vazios e com <br> como espaçadores
          blocks.push({
            type: 'paragraph',
            text: (!pContent || pContent === '<br>') ? '' : element.innerHTML,
            align: pAlign
          });
          break;
        }

        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          if (element.textContent?.trim()) {
            // Extrair text-align do style
            const hStyle = element.getAttribute('style') || '';
            const hAlignMatch = hStyle.match(/text-align:\s*(left|center|right|justify)/);
            const hAlign = hAlignMatch ? hAlignMatch[1] as 'left' | 'center' | 'right' | 'justify' : undefined;
            
            blocks.push({
              type: 'heading',
              level: parseInt(tagName[1]) as 1 | 2 | 3 | 4 | 5 | 6,
              text: element.innerHTML, // Usar innerHTML para preservar formatação
              align: hAlign
            });
          }
          break;

        case 'ul':
        case 'ol':
          const items: string[] = [];
          element.querySelectorAll('li').forEach(li => {
            if (li.textContent?.trim() || li.querySelector('img')) {
              items.push(li.innerHTML);
            }
          });
          if (items.length > 0) {
            blocks.push({
              type: 'list',
              ordered: tagName === 'ol',
              items
            });
          }
          break;

        case 'blockquote':
          const quoteP = element.querySelector('p');
          const cite = element.querySelector('cite');
          if (quoteP?.textContent?.trim()) {
            blocks.push({
              type: 'quote',
              quote: quoteP.innerHTML, // Usar innerHTML para preservar formatação
              author: cite?.textContent || undefined
            });
          }
          break;

        case 'img':
          blocks.push({
            type: 'image',
            url: element.getAttribute('src') || '',
            alt: element.getAttribute('alt') || ''
          });
          break;

        case 'div':
          // Handle custom alerts and blog CTA card
          const className = element.className;
          const dataType = element.getAttribute('data-type');
          if (dataType === 'blog-cta-card') {
            blocks.push({
              type: 'paragraph',
              text: element.outerHTML,
            });
          } else if (className.includes('alert-')) {
            const variant = className.replace('alert-', '') as 'info' | 'warning' | 'success' | 'tip';
            if (element.textContent?.trim()) {
              blocks.push({
                type: 'alert',
                variant,
                text: element.innerHTML // Usar innerHTML para preservar formatação
              });
            }
          }
          break;

        case 'hr':
          blocks.push({
            type: 'separator'
          });
          break;
      }
    }
  };

  // Process all child nodes
  doc.body.childNodes.forEach(processNode);

  return blocks;
};
